import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import API from '@/apiRequest';

/**
 * Maps the individual order response (from /view/orders/ID)
 * to the flat structure expected by the OrdersList (from /orders).
 * This is used for "post-processing" when we need to insert a view-model
 * into a list-model data structure.
 */
export const mapOrderToListView = order => {
  if (!order) return null;

  return {
    ...order,
    identifier: order.identifier || order.form?.details?.building_id || '',
    client: order.client || order.form?.clients?.client || '',
    inspector: order.inspector || order.form?.parties_involved?.inspector?.name || '',
    inspector_document: order.inspector_document || order.form?.parties_involved?.inspector?.document || '',
    building_type: order.building_type || order.form?.details?.building_type || '',
    schedule: order.schedule || (order.form?.schedule?.date
      ? `${order.form.schedule.date} ${order.form.schedule.period || ''}`.trim()
      : null),
    created_at: order.created_at?.date || order.created_at,
    franchisee_id: order.franchisee_id || order.form?.franchisees?.franchisee_id,
    client_franchisee_id: order.client_franchisee_id,
    franchisee_name: order.franchisee_name || order.form?.franchisees?.franchisee,
    tags: order.tags || order.form?.tags?.tags || [],
    credits: order.credits ?? 0,
    urgency: order.urgency || order.form?.additionals?.urgency,
    modality: order.modality || order.form?.additionals?.modality,
    is_archived: order.is_archived ?? false,
  };
};

export const getOrderById = async (orderId, isInspection = false) => {
  const endpoint = isInspection ? `/view/inspections/${orderId}` : `/view/orders/${orderId}`;
  const { data } = await API.get(endpoint);
  return data; // Return RAW data
};

export const useOrder = (orderId, options = {}) => {
  const { pathname } = useLocation();
  const isInspection = pathname.includes('/inspections');

  return useQuery({
    queryKey: [isInspection ? 'inspection' : 'order', orderId],
    queryFn: () => getOrderById(orderId, isInspection),
    enabled: !!orderId,
    staleTime: 3000,
    ...options,
  });
};

export const useOrderCroquiUploadUri = (orderId, extension, options = {}) => {
  return useQuery({
    queryKey: ['order', orderId, 'croqui-upload-uri', extension],
    queryFn: async () => {
      const { data } = await API.get(`/orders/${orderId}/croqui-upload-uri?file_extension=${extension}`);
      return data.uri;
    },
    enabled: !!orderId && !!extension && !!options.enabled,
    staleTime: 0, // Signed URIs expire
    ...options,
  });
};

export const getOrderRepairBudget = async orderId => {
  const { data } = await API.get(`/orders/${orderId}/repair-budget`);
  return data;
};

export const useOrderRepairBudget = (orderId, options = {}) => {
  return useQuery({
    queryKey: ['order', orderId, 'repair-budget'],
    queryFn: () => getOrderRepairBudget(orderId),
    enabled: !!orderId,
    ...options,
  });
};
